import { PlanCapabilitiesService } from '@shared/services/plan-capabilities.service';
import { menuDoPlano } from '../aside/menu-do-plano';
import { HelpTelemetryService } from '@core/services/help/help-telemetry.service';
import { Injectable, inject } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ApiService } from '@core/services/api';
import { TenancyService } from '@core/services/common';
import { PermissionService } from '@shared/services/permission.service';
import { MenuConfig } from '@core/_config/menu.config';
import { ItemDeMenu } from '../aside/menu.model';
import {
  GrupoDeResultado,
  MINIMO_DE_CARACTERES,
  ResultadoDeBusca,
  TIPOS,
  TIPOS_DE_ENTIDADE,
  TipoDeBusca,
} from './busca-global.model';

/** O que as quatro rotas devolvem em comum: um envelope paginado com `data`. */
interface Paginado<T> { data: T[]; }

/** O mínimo que as quatro entidades têm em comum para virar linha da paleta. */
interface RegistroBuscavel {
  id: string;
  name: string;
  fantasy_name?: string;
  cpf?: string;
  cnpj?: string;
  occupation?: string;
  course?: string;
}

@Injectable({ providedIn: 'root' })
export class BuscaGlobalService {
  private readonly telemetria = inject(HelpTelemetryService);
  private readonly consultasMedidas = new Set<string>();
  private readonly api = inject(ApiService);
  private readonly permissoes = inject(PermissionService);
  private readonly tenancy = inject(TenancyService);
  private readonly plano = inject(PlanCapabilitiesService);

  /**
   * Os tipos de ENTIDADE que este usuário pode ver, decididos ANTES de
   * qualquer chamada.
   *
   * Gatear antes, e não filtrar depois, é o que impede o vazamento por
   * construção: o payload que o usuário não pode ver nunca chega ao
   * navegador, então nenhum bug futuro de renderização o expõe. O custo do
   * desenho oposto seria uma chamada por tipo proibido, jogada fora, e a
   * confiança de que a API barra — e o índice de segurança deste ecossistema
   * registra 14 lacunas P1 de authz na API.
   *
   * `checkPermissions` lê o `NgxPermissionsStore` de forma SÍNCRONA e já
   * resolve o superusuário `'*'` sozinho (BUG-CLI-122). Passar `'*'` na lista
   * mantém a forma que o menu e as rotas usam.
   *
   * O CENTRAL fica fora inteiro: `clients`, `providers`, `employees` e
   * `interns` são rotas de tenant. Lá a permissão sobra e o banco é que não
   * tem as tabelas.
   */
  tiposPermitidos(): TipoDeBusca[] {
    if (!this.tenancy.isSubdomain) {
      return [];
    }

    return TIPOS_DE_ENTIDADE.filter(tipo => this.permissoes.checkPermissions([...TIPOS[tipo].permissao]));
  }

  /**
   * Um termo, N chamadas paralelas — uma por tipo PERMITIDO, nunca mais.
   *
   * Grupo vazio não entra no resultado: a paleta mostra cabeçalho de grupo, e
   * cabeçalho sem linha é ruído.
   *
   * `catchError` por tipo, e não no `forkJoin`: um endpoint fora do ar
   * derrubaria os outros três, e a paleta ficaria em branco por causa de uma
   * entidade só. Erro vira grupo vazio; o console do interceptor já registra.
   */
  buscar(termo: string, medirSemResultado = false): Observable<GrupoDeResultado[]> {
    const busca = termo.trim();
    const doMenu = this.buscarNoMenu(busca);
    const naFrente = doMenu ? [doMenu] : [];
    const tipos = busca.length >= MINIMO_DE_CARACTERES ? this.tiposPermitidos() : [];

    if (tipos.length === 0) {
      return of(naFrente);
    }

    const chamadas = tipos.map(tipo =>
      this.chamar(tipo, busca).pipe(
        map(pagina => ({ grupo: this.paraGrupo(tipo, pagina.data ?? []), sucesso: true })),
        catchError(() => of({ grupo: this.paraGrupo(tipo, []), sucesso: false }))
      )
    );

    // O menu vem PRIMEIRO: é navegação, é instantâneo, e é o que o usuário
    // quer quando digita o nome de uma tela.
    return forkJoin(chamadas).pipe(map(respostas => {
      const grupos = [...naFrente, ...respostas.map(resposta => resposta.grupo).filter(grupo => grupo.itens.length > 0)];
      const chave = this.semAcento(busca);
      if (medirSemResultado && this.tenancy.isSubdomain && grupos.length === 0
        && respostas.every(resposta => resposta.sucesso) && !this.consultasMedidas.has(chave)) {
        if (this.consultasMedidas.size >= 100) {
          const primeira = this.consultasMedidas.values().next().value;
          if (primeira !== undefined) this.consultasMedidas.delete(primeira);
        }
        this.consultasMedidas.add(chave);
        this.telemetria.registrar({ surface: 'busca_global', outcome: 'sem_resultado', query: busca, query_kind: 'entity' });
      }
      return grupos;
    }));
  }

  /**
   * O quinto tipo do D-5. Não é chamada: o menu é código do produto, lido do
   * MESMO lugar que a casca lê — `MenuConfig().configs.aside`,
   * `subdomain_items` no tenant e `items` no central.
   *
   * A permissão é a MESMA do menu lateral, item por item. Item sem
   * `permission` é público — é o caso do "Início" —, e continua aparecendo.
   *
   * Comparação sem acento e sem caixa: quem digita "funcionarios" tem de achar
   * "Funcionários". `normalize('NFD')` mais a faixa de diacríticos é o mesmo
   * recurso que a plataforma já oferece; não vale acrescentar dependência
   * para isto.
   */
  buscarNoMenu(termo: string): GrupoDeResultado | null {
    const busca = this.semAcento(termo);

    if (busca.length < MINIMO_DE_CARACTERES) {
      return null;
    }

    const menus = new MenuConfig().configs.aside;
    const raiz = (this.tenancy.isSubdomain ? menus.subdomain_items : menus.items) as ItemDeMenu[];

    const itens = this.achatar(this.tenancy.isSubdomain ? menuDoPlano(raiz, this.plano.modulos) : raiz)
      .filter(item => this.semAcento(item.title ?? '').includes(busca))
      .map<ResultadoDeBusca>(item => ({
        tipo: 'menu',
        id: item.page as string,
        titulo: item.title as string,
        subtitulo: null,
        rota: item.page as string,
      }));

    if (itens.length === 0) {
      return null;
    }

    const meta = TIPOS.menu;
    return { tipo: 'menu', rotulo: meta.rotulo, icone: meta.icone, itens };
  }

  /**
   * Achata a árvore mantendo só o que É destino (tem `page`) e o que o
   * usuário pode ver.
   *
   * A recursão não é opcional: "Estagiários", "Funcionários" e "Sócios" vivem
   * DENTRO do grupo "Pessoas" (`administrative.section.ts`, chamado
   * "Colaboradores" até 06/09/2026), e varredura rasa não os enxergaria. O grupo em si não tem `page` e por isso não vira
   * resultado — não há para onde navegar.
   */
  private achatar(itens: ItemDeMenu[]): ItemDeMenu[] {
    return itens.flatMap(item => {
      if (item.permission && !this.permissoes.checkPermissions(item.permission)) {
        return [];
      }

      const filhos = item.submenu ? this.achatar(item.submenu) : [];
      return item.page && item.title ? [item, ...filhos] : filhos;
    });
  }

  private semAcento(texto: string): string {
    // A faixa vai ESCAPADA (\u0300-\u036f) de proposito: escrever os
    // combinantes literalmente deixa o arquivo a merce de qualquer editor que
    // normalize o fonte, e o bug resultante e' invisivel na revisao.
    return texto.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
  }

  /**
   * As quatro rotas já aceitam `search` no contrato
   * (`route-contracts.ts:41,75,112,145`) e já o repassam por `toHttpParams`.
   * `per_page: 5` é o que cabe na paleta sem virar listagem — quem quer a
   * lista inteira vai para a tela, que é o destino do resultado.
   */
  private chamar(tipo: TipoDeBusca, search: string): Observable<Paginado<RegistroBuscavel>> {
    const params = { search, per_page: 5, page: 1 };

    switch (tipo) {
      case 'cliente':
        return this.api.clients.getAll(params) as unknown as Observable<Paginado<RegistroBuscavel>>;
      case 'fornecedor':
        return this.api.providers.getAll(params) as unknown as Observable<Paginado<RegistroBuscavel>>;
      case 'funcionario':
        return this.api.employees.getAll(params) as unknown as Observable<Paginado<RegistroBuscavel>>;
      default:
        return this.api.interns.getAll(params) as unknown as Observable<Paginado<RegistroBuscavel>>;
    }
  }

  private paraGrupo(tipo: TipoDeBusca, registros: RegistroBuscavel[]): GrupoDeResultado {
    const meta = TIPOS[tipo];

    return {
      tipo,
      rotulo: meta.rotulo,
      icone: meta.icone,
      itens: registros.map(registro => this.paraResultado(tipo, registro)),
    };
  }

  private paraResultado(tipo: TipoDeBusca, registro: RegistroBuscavel): ResultadoDeBusca {
    return {
      tipo,
      id: registro.id,
      titulo: registro.name,
      subtitulo: registro.fantasy_name || registro.occupation || registro.course || registro.cnpj || registro.cpf || null,
      // O destino definitivo é decidido em `rotaDe` (segundo gate), que
      // conhece a permissão de escrita. Até lá, a listagem — que é o que a
      // permissão de VER garante.
      rota: TIPOS[tipo].listagem,
    };
  }

  /**
   * O SEGUNDO gate — o de destino.
   *
   * As quatro rotas `:id/editar` exigem `X_Update` (medido: os quatro
   * `*-routing.module.ts` de tenant usam a mesma forma). Mandar para lá quem
   * só tem `X_View` faz o `NgxPermissionsGuard` redirecionar para
   * `/painel/dashboard`: o usuário clica num resultado e cai no dashboard,
   * sem entender por quê.
   *
   * Quem não edita vai para a LISTAGEM, que é exatamente o que a permissão de
   * ver garante. Não existe URL de "visualizar" nesta base — a visualização é
   * diálogo dentro da listagem —, e inventar uma seria mobília nova, fora do
   * D-5.
   */
  rotaDe(resultado: ResultadoDeBusca): string {
    if (resultado.tipo === 'menu') {
      return resultado.rota;
    }

    const meta = TIPOS[resultado.tipo];
    const podeEditar = this.permissoes.checkPermissions([...meta.permissaoDeEdicao]);

    return podeEditar ? `${meta.listagem}/${resultado.id}/editar` : meta.listagem;
  }
}
